// Detects a password-protected PDF the moment it's chosen, asks for the
// password once, and hands the tool a decrypted copy -- so every PDF tool
// works on it and the output is an ordinary unprotected file. (Confirmed
// as a real, site-wide problem before this existed: pdf-lib can't read
// encrypted files at all, so Merge failed outright, Sign/Split/etc. threw
// or produced files people then couldn't open.)
//
// The decrypting library (@cantoo/pdf-lib, a pdf-lib fork that supports
// passwords) is only ever loaded via dynamic import when an encrypted
// file is actually found -- everyone else pays nothing for it.

export class WrongPdfPasswordError extends Error {
  constructor() {
    super("Incorrect PDF password");
    this.name = "WrongPdfPasswordError";
  }
}

/** Cheap pre-check: an encrypted PDF always carries an /Encrypt entry in
 * its trailer (or, for linearized files, near the start). Scanning just
 * the first 2KB and last 16KB avoids parsing every PDF a person picks --
 * a 50MB file shouldn't be fully parsed just to learn it isn't locked.
 * A false positive (the text "/Encrypt" appearing in ordinary content) is
 * caught by the real check in isActuallyEncrypted; a false negative just
 * falls back to each tool's existing behavior. */
export async function looksEncrypted(file: File): Promise<boolean> {
  const decoder = new TextDecoder("latin1");
  const head = decoder.decode(await file.slice(0, 2048).arrayBuffer());
  if (head.includes("/Encrypt")) return true;
  const tail = decoder.decode(await file.slice(Math.max(0, file.size - 16384)).arrayBuffer());
  return tail.includes("/Encrypt");
}

async function isActuallyEncrypted(file: File): Promise<boolean> {
  const { PDFDocument } = await import("@cantoo/pdf-lib");
  try {
    const doc = await PDFDocument.load(await file.arrayBuffer(), { ignoreEncryption: true, updateMetadata: false });
    return doc.isEncrypted;
  } catch {
    return false; // too damaged to tell -- leave it to the tool's own error handling
  }
}

/**
 * The library reads the document-information strings (title, author...) of an encrypted file back as
 * empty, so they are copied over from pdf.js, which decrypts them correctly. Best effort.
 */
async function restoreInfo(doc: { getTitle(): string | undefined; getAuthor(): string | undefined; getSubject(): string | undefined; getKeywords(): string | undefined; setTitle(v: string): void; setAuthor(v: string): void; setSubject(v: string): void; setKeywords(v: string[]): void }, bytes: Uint8Array, password: string): Promise<void> {
  try {
    const { openPdfBytes } = await import("./utils");
    const pdf = await openPdfBytes(bytes, password);
    const info = ((await pdf.getMetadata()).info ?? {}) as Record<string, string | undefined>;
    if (info.Title && !doc.getTitle()) doc.setTitle(info.Title);
    if (info.Author && !doc.getAuthor()) doc.setAuthor(info.Author);
    if (info.Subject && !doc.getSubject()) doc.setSubject(info.Subject);
    if (info.Keywords && !doc.getKeywords()) doc.setKeywords([info.Keywords]);
  } catch {
    /* the pages are what matters */
  }
}

/** Returns a decrypted copy of the file (same name), or throws
 * WrongPdfPasswordError. An empty password is valid and common: many
 * "locked" PDFs only restrict printing/copying and open without one. */
export async function decryptPdf(file: File, password: string): Promise<File> {
  const { PDFDocument } = await import("@cantoo/pdf-lib");
  let doc;
  try {
    doc = await PDFDocument.load(await file.arrayBuffer(), { password, updateMetadata: false });
  } catch (err) {
    // The library words "that password doesn't open it" two ways:
    // "Password incorrect" for a non-empty wrong password, and
    // "NEEDS PASSWORD" when the (empty) password we try silently first
    // isn't enough -- found by running it, not from the docs. Both
    // simply mean: ask the person.
    if (err instanceof Error && /password incorrect|needs password/i.test(err.message)) throw new WrongPdfPasswordError();
    throw err;
  }
  await restoreInfo(doc, new Uint8Array(await file.arrayBuffer()), password);
  const bytes = await doc.save();
  // The copy must open with plain pdf-lib, which is what every tool uses. Say so now, at the drop,
  // instead of letting a tool fail later with "the file may be damaged" (QA RM-1).
  try {
    const { PDFDocument: PlainDocument } = await import("pdf-lib");
    const check = await PlainDocument.load(bytes, { updateMetadata: false });
    if (check.isEncrypted) throw new Error("still encrypted");
  } catch {
    throw new UnsupportedPdfProtectionError();
  }
  return new File([new Uint8Array(bytes)], file.name, { type: "application/pdf", lastModified: file.lastModified });
}

export class UnsupportedPdfProtectionError extends Error {
  constructor() {
    super("This PDF's protection could not be removed here");
    this.name = "UnsupportedPdfProtectionError";
  }
}

/** Resolves to the password, or null if the person skips this file. */
// Password-protected files the person chose to skip. FileDropZone normally drops them; a tool that
// opts in (Merge PDF: keepSkippedProtectedFiles) gets them in its list instead, so they can be
// unlocked later without picking the file again. Held weakly -- it's only ever "is THIS file object
// one of those", and it must not keep a large file alive.
const skippedLocked = new WeakSet<File>();
export const markSkippedLocked = (file: File) => void skippedLocked.add(file);
export const isSkippedLocked = (file: File) => skippedLocked.has(file);

// Files that came out of unlockPdfIfNeeded as "unlocked". The Unlock PDF tool uses this to tell
// "the password was removed" from "this file was never protected".
const unlockedFiles = new WeakSet<File>();
export const isUnlockedCopy = (file: File) => unlockedFiles.has(file);
const asUnlocked = (file: File): UnlockResult => { unlockedFiles.add(file); return { status: "unlocked", file }; };

export type PasswordRequester = (fileName: string, previousAttemptWasWrong: boolean) => Promise<string | null>;

export type UnlockResult =
  | { status: "unchanged"; file: File }
  | { status: "unlocked"; file: File }
  | { status: "cancelled" }
  | { status: "failed" };

export async function unlockPdfIfNeeded(file: File, requestPassword: PasswordRequester): Promise<UnlockResult> {
  if (!(await looksEncrypted(file))) return { status: "unchanged", file };
  if (!(await isActuallyEncrypted(file))) return { status: "unchanged", file };

  // Owner-password-only files open with an empty user password -- no
  // reason to interrupt anyone for those.
  try {
    return asUnlocked(await decryptPdf(file, ""));
  } catch (err) {
    if (!(err instanceof WrongPdfPasswordError)) return { status: "failed" };
  }

  let wrong = false;
  for (;;) {
    const password = await requestPassword(file.name, wrong);
    if (password === null) return { status: "cancelled" };
    try {
      return asUnlocked(await decryptPdf(file, password));
    } catch (err) {
      if (!(err instanceof WrongPdfPasswordError)) return { status: "failed" };
      wrong = true;
    }
  }
}
